/**
 * Synthesize a new `QueryFieldRow` from a picker selection.
 *
 * The takeover holds `fieldRows: QueryFieldRow[]` (`display` + `raw`)
 * and Save serializes the `raw` companion via `toInput` in
 * `fieldsMapper.ts`. The picker's job is to produce raw+display pairs
 * that pass through that same write path without special-casing.
 *
 * Synthetic ids are negative numerics so the rest of the takeover (id
 * keying, panel-open, panel-close) keeps working with stable handles,
 * and the BE never sees them — `toInput` strips `id` on the way out.
 *
 * The slideout inserts one row per click, so this module exports a
 * single `synthesizeQueryFieldRow` rather than a batch helper.
 */

import {
  deriveLevelColumnDisplay,
  deriveLevelFullDisplay,
} from "../../../../shared/levels/levelDisplay";
import { BLANK_RAW_SETTINGS } from "../FieldSettingsPanel/fieldSettingsMapper";
import { deriveIsTransactionLevel } from "../fieldsMapper";
import type { QueryRollupLevelOption } from "../../../../shared/levels/types";
import type { QueryFieldRow } from "../fieldsMapper";
import type { Selection } from "./types";
import type {
  FieldDataType,
  QueryFieldFieldsFragment,
  QueryFieldKind,
} from "~/graphql/supergraphTypes";

/** Best-effort map from upstream Semantic Layer `QueryAttributeType.type`
 *  (free-form string) to `FieldDataType`. Unknown / unrecognized types
 *  fall through to `TEXT` — the safest default for an unknown column,
 *  since `TEXT` doesn't drive any of the numeric formatting controls. */
const ATTRIBUTE_TYPE_TO_FIELD_DATA_TYPE: Readonly<Record<string, FieldDataType>> = {
  STRING: "TEXT",
  TEXT: "TEXT",
  VARCHAR: "TEXT",
  CHAR: "TEXT",
  BOOLEAN: "TEXT",
  DATE: "DATE",
  TIMESTAMP: "DATE",
  DATETIME: "DATE",
  TIME: "DATE",
  NUMBER: "QUANTITY",
  NUMERIC: "QUANTITY",
  INTEGER: "QUANTITY",
  INT: "QUANTITY",
  BIGINT: "QUANTITY",
  SMALLINT: "QUANTITY",
  FLOAT: "QUANTITY",
  DOUBLE: "QUANTITY",
  DECIMAL: "QUANTITY",
};

const toFieldDataType = (attributeType: string): FieldDataType =>
  ATTRIBUTE_TYPE_TO_FIELD_DATA_TYPE[attributeType.trim().toUpperCase()] ?? "TEXT";

/** Monotonically decreasing counter for synthetic row ids. Module scope
 *  ensures the value never resets between picker open/close cycles or after
 *  a row is deleted — preventing id collisions when `fieldRows.length` is
 *  reused as a basis after a delete. */
let syntheticIdCounter = 0;

const makeBaseRaw = (params: {
  syntheticId: number;
  kind: QueryFieldKind;
  alias: string;
  sortOrder: number;
  levels: string[] | null;
}): QueryFieldFieldsFragment => ({
  __typename: "QueryFieldType" as const,
  id: params.syntheticId,
  queryVersionId: 0,
  referenceKind: params.kind,
  alias: params.alias,
  // The backend allocates the tag on save; an empty value is what tells the
  // write path to omit `mergeField` so a fresh suffix is issued.
  mergeField: "",
  typeHierarchyNodeId: null,
  formulaId: null,
  primaryField: null,
  primaryFieldId: null,
  dynamicDate: null,
  sortOrder: params.sortOrder,
  settings: null,
  datePeriod: null,
  levels: { __typename: "LevelStackType" as const, levels: params.levels ?? [] },
  components: [],
});

const synthesizeRaw = (
  selection: Selection,
  syntheticId: number,
  sortOrder: number,
  defaultRollupLevel: string,
): QueryFieldFieldsFragment => {
  if (selection.kind === "node") {
    return {
      ...makeBaseRaw({
        syntheticId,
        kind: "NODE",
        alias: selection.name,
        sortOrder,
        levels: defaultRollupLevel ? [defaultRollupLevel] : [],
      }),
      typeHierarchyNodeId: selection.typeHierarchyNodeId,
    };
  }
  if (selection.kind === "formula") {
    return {
      ...makeBaseRaw({
        syntheticId,
        kind: "FORMULA",
        alias: selection.name,
        sortOrder,
        levels: [],
      }),
      formulaId: selection.formulaId,
    };
  }
  if (selection.kind === "dynamicDate") {
    return {
      ...makeBaseRaw({
        syntheticId,
        kind: "DYNAMIC_DATE",
        alias: selection.name,
        sortOrder,
        levels: [],
      }),
      dynamicDate: selection.dynamicDate,
      settings: { ...BLANK_RAW_SETTINGS, fieldType: "DATE" },
    };
  }
  return {
    ...makeBaseRaw({
      syntheticId,
      kind: "PRIMARY_FIELD",
      alias: selection.name,
      sortOrder,
      levels: [],
    }),
    primaryField: selection.primaryField,
    // Seed the field type from the upstream attribute so opening the
    // settings panel doesn't silently default a STRING / DATE column to
    // NUMBER. Without this, saving any unrelated edit (alias, rules)
    // through `applyFieldSettingsToRaw` would persist `fieldType: NUMBER`
    // for non-numeric primary fields.
    settings: { ...BLANK_RAW_SETTINGS, fieldType: toFieldDataType(selection.attributeType) },
  };
};

/** Light-touch display row for a freshly-synthesized field. We skip
 *  `toDisplay` from `fieldsMapper` because it derives `dataFormat` /
 *  `level` / `dateRange` from settings + levels that we don't have yet.
 *
 *  The display union is intentionally narrow in older typings, but saved
 *  hierarchy levels can carry arbitrary labels; `deriveLevelColumnDisplay`
 *  renders the labelled stack for the grid.
 */
const FIELD_TYPE_LABEL: Record<QueryFieldKind, QueryFieldRow["display"]["fieldType"]> = {
  NODE: "Node",
  FORMULA: "Formula",
  FORMULA_COMPONENT: "Formula",
  PRIMARY_FIELD: "Primary field",
  DYNAMIC_DATE: "Dynamic date",
};

const defaultDataTypeForKind = (
  raw: QueryFieldFieldsFragment,
): QueryFieldRow["display"]["dataType"] => {
  if (raw.settings?.fieldType) return raw.settings.fieldType;
  if (raw.referenceKind === "DYNAMIC_DATE") return "DATE";
  if (raw.referenceKind === "NODE") return "VALUE";
  if (raw.referenceKind !== "PRIMARY_FIELD") return "QUANTITY";
  return null;
};

const synthesizeDisplay = (
  raw: QueryFieldFieldsFragment,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>,
): QueryFieldRow["display"] => {
  const isNode = raw.referenceKind === "NODE";
  return {
    id: String(raw.id),
    name: raw.alias,
    // Both placeholders: the grid recomputes `variant` from row order once
    // the row is appended, and `mergeField` stays empty until the backend
    // allocates the tag on save.
    variant: 1,
    mergeField: raw.mergeField,
    fieldType: FIELD_TYPE_LABEL[raw.referenceKind],
    dataType: defaultDataTypeForKind(raw),
    dateRange: "--",
    hasDateOffset: false,
    offsetMonths: 0,
    level: isNode
      ? deriveLevelColumnDisplay(raw.levels?.levels ?? [], rollupLevelOptions)
      : "--",
    levelFull: isNode
      ? deriveLevelFullDisplay(raw.levels?.levels ?? [], rollupLevelOptions)
      : "--",
    isTransactionLevel: deriveIsTransactionLevel(raw.levels),
    dataFormat: "Default",
    filterRules: "--",
    suppressionRules: "--",
  };
};

/** Convert a single picker selection into a new `QueryFieldRow`, ready
 *  for the takeover to append. `nextSortOrder` should be the next free
 *  slot at the end of the existing field list (`fieldRows.length`).
 *
 *  Synthetic ids come from `syntheticIdCounter` (module-level, never
 *  resets) so deleting a row and inserting another can never produce
 *  the same id — `fieldRows.length` alone is not safe after a delete.
 */
export const synthesizeQueryFieldRow = (
  selection: Selection,
  nextSortOrder: number,
  defaultRollupLevel: string,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>,
): QueryFieldRow => {
  const syntheticId = --syntheticIdCounter;
  const raw = synthesizeRaw(selection, syntheticId, nextSortOrder, defaultRollupLevel);
  return { display: synthesizeDisplay(raw, rollupLevelOptions), raw };
};
